import { useCallback, VoidFunctionComponent } from 'react';
import { PerspectiveCamera, Environment } from '@react-three/drei';
import { Vector3 } from 'three';
import { OnlyBotsLogo } from '@/components/three/OnlyBotsLogo';
import { Spheres } from '@/components/three/Spheres';
import { useMotionParallax } from '@/hooks/useMotionParallax';
import { useEnterOrSpace } from '@/hooks/useUserInput';
import { garamondNarrowBook } from '@/lib/fonts';
import { CycleOnlyBot } from '@/components/CycleOnlyBot';
import { offblack } from '@/lib/colors';
import { ClickableText } from '@/components/ClickableText';
import { FeaturedBot } from '@/lib/types';
import startSound from '@/sounds/ob-sfx-game-start.mp3';
import continueSound from '@/sounds/ob-sfx-commit.mp3';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';
import { buildPublicUrl } from '@/lib/globals';
import { useSecondaryPreloader } from '@/hooks/usePreloader';
import { useResponsive } from '@/hooks/useResponsive';

const target = new Vector3(0, 0, 0);
const initialPosition = new Vector3(0, 0, 7);

export const colorEnvironmentFile = buildPublicUrl('hdri/onlybots-scene-2k.hdr');
export const bnwEnvironmentFile = buildPublicUrl('hdri/onlybots-scene-bw-1k.hdr');

interface Props {
    goToNextScene: () => void;
    continueClick: () => void;
    featuredBots?: FeaturedBot[];
}

export const TitleScreen: VoidFunctionComponent<Props> = ({ goToNextScene, continueClick, featuredBots }) => {
    useSecondaryPreloader();

    const { setOrientationOrigin } = useMotionParallax(target, initialPosition, [0, -5], [0, 2], [0, -10], [0, -5]);
    const [playStartSound] = useOnlybotsSound(startSound);
    const [playContinueSound] = useOnlybotsSound(continueSound);
    const { responsiveValue } = useResponsive();

    useEnterOrSpace(goToNextScene);

    const onStartClick = useCallback(() => {
        playStartSound();
        goToNextScene();
    }, [goToNextScene, playStartSound]);

    const onContinueClick = useCallback(() => {
        playContinueSound();
        continueClick();
    }, [continueClick, playContinueSound]);

    return (
        <>
            <directionalLight intensity={3} castShadow={false} position={[-1, -1, 0.3]} />
            <directionalLight intensity={3} castShadow={false} position={[-10, 10, 10]} />
            <PerspectiveCamera near={0.1} far={1000} fov={94.5} position={initialPosition} makeDefault>
                <ClickableText
                    font={garamondNarrowBook}
                    fontSize={0.025}
                    letterSpacing={-0.06}
                    color={offblack}
                    anchorX="center"
                    position={[0, -0.14, -0.2]}
                    onClick={onStartClick}
                >
                    START
                </ClickableText>
                <ClickableText
                    font={garamondNarrowBook}
                    fontSize={0.025}
                    letterSpacing={-0.06}
                    color={offblack}
                    anchorX="center"
                    position={[0, -0.18, -0.2]}
                    onClick={onContinueClick}
                >
                    CONTINUE
                </ClickableText>
            </PerspectiveCamera>
            <group onClick={setOrientationOrigin}>
                <Environment background={true} near={0.1} far={1000} files={colorEnvironmentFile} />
                <Environment background={false} files={bnwEnvironmentFile} />
                <group scale={0.25} position={[0, -0.5, 0]}>
                    {featuredBots && featuredBots.length > 1 && <CycleOnlyBot featuredBots={featuredBots} />}
                </group>
                {/* // @TODO these fell behind the environment somehow. */}
                <Spheres />
                <OnlyBotsLogo
                    rotation={[Math.PI / 16, Math.PI / 12, 0]}
                    scale={responsiveValue(12, 8.5)}
                    position={[0.4, 2.76, 1]}
                />
            </group>
        </>
    );
};
